import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Settings, ArrowLeft } from "lucide-react";
import { useDir } from "@/lib/nav";
import { cn } from "@/lib/utils";
import { SandboxBanner } from "@/components/SandboxBanner";

// Opacity-only crossfade. We deliberately avoid an x-translate + will-change:transform
// here: a translate leaves two full-screen shells offset side-by-side when a transition
// is interrupted (the "two screens overlapping" bug), and a transform turns the shell
// into the containing block for position:fixed (which clipped the photo lightbox).
const pageVariants = {
  initial: { opacity: 0 },
  enter: { opacity: 1 },
  exit: { opacity: 0 },
};
const pageTransition = { duration: 0.18, ease: "easeOut" };

export function AppShell({ children, title, onBack, right }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dir = useDir();
  const scrollRef = useRef(null);
  const swipeRef = useRef(null);
  const { scrollY } = useScroll({ container: scrollRef });
  const titleOpacity = useTransform(scrollY, [12, 52], [0, 1]);
  const logoOpacity = useTransform(scrollY, [12, 52], [1, 0]);
  const borderOpacity = useTransform(scrollY, [0, 40], [0, 1]);

  // iOS-style left-edge swipe-back — only when the screen has a back target.
  const onTouchStart = (e) => {
    const t = e.touches[0];
    swipeRef.current = t.clientX <= 28 ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchMove = (e) => {
    if (!swipeRef.current || !onBack) return;
    const t = e.touches[0];
    const dx = t.clientX - swipeRef.current.x;
    const dy = Math.abs(t.clientY - swipeRef.current.y);
    if (dx > 64 && dy < 44) { swipeRef.current = null; onBack(); }
  };
  const onTouchEnd = () => { swipeRef.current = null; };

  return (
    <motion.div
      custom={dir}
      variants={pageVariants}
      initial="initial"
      animate="enter"
      exit="exit"
      transition={pageTransition}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className="screen-glow fixed inset-0 z-10 flex flex-col bg-background text-foreground"
    >
      <div className="mx-auto flex h-full w-full max-w-md flex-col md:max-w-5xl">
        {/* Desktop header */}
        <header className="sticky top-0 z-30 hidden items-center justify-between border-b border-border bg-card px-8 py-4 md:flex">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2" data-testid="header-logo">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-8 w-8 rounded-lg" />
            <span className="font-heading text-lg font-bold tracking-tight">Send-Proof</span>
          </button>
          <nav className="flex items-center gap-1">
            <button
              data-testid="nav-jobs"
              onClick={() => navigate("/dashboard")}
              className={cn("rounded-lg px-3 py-2 text-sm font-medium", pathname === "/dashboard" ? "bg-emerald-500/10 text-emerald-500" : "text-muted-foreground hover:bg-muted")}
            >
              Jobs
            </button>
            <button
              data-testid="nav-account"
              onClick={() => navigate("/account")}
              className={cn("rounded-lg px-3 py-2 text-sm font-medium", pathname === "/account" ? "bg-emerald-500/10 text-emerald-500" : "text-muted-foreground hover:bg-muted")}
            >
              <Settings className="mr-1 inline h-4 w-4" />Settings
            </button>
          </nav>
        </header>

        {/* Mobile app bar — logo centered (fades out on scroll), title fades in */}
        <div className="relative flex min-h-[56px] shrink-0 items-center px-2 pt-[env(safe-area-inset-top)] md:hidden">
          <motion.div className="pointer-events-none absolute inset-0 border-b border-border bg-card/80 backdrop-blur-xl" style={{ opacity: borderOpacity }} />

          {/* Centered stack: logo (default) cross-fading with the scroll title */}
          <div className="absolute inset-x-0 bottom-0 top-[env(safe-area-inset-top)] z-10 flex items-center justify-center">
            <motion.button
              onClick={() => navigate("/dashboard")}
              style={{ opacity: logoOpacity }}
              aria-label="Send-Proof home"
              data-testid="appbar-logo"
              className="absolute transition-transform active:scale-90"
            >
              <img src="/logo-mark.webp" alt="Send-Proof" className="h-11 w-11 rounded-xl" />
            </motion.button>
            <motion.div
              style={{ opacity: titleOpacity }}
              className="pointer-events-none max-w-[62%] truncate text-center font-heading text-base font-bold"
              data-testid="appbar-title"
            >
              {title}
            </motion.div>
          </div>

          {/* Left: back button (when present) */}
          <div className="relative z-20 flex w-16 items-center">
            {onBack && (
              <button onClick={onBack} data-testid="appbar-back" aria-label="Back" className="flex h-9 w-9 items-center justify-center rounded-full text-foreground transition-transform active:scale-90">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="flex-1" />
          {/* Right: optional page slot + shared Settings gear */}
          <div className="relative z-20 flex w-16 items-center justify-end gap-1 pr-1">
            {right}
            {pathname !== "/account" && (
              <button
                onClick={() => navigate("/account")}
                data-testid="appbar-settings"
                aria-label="Settings"
                className="flex h-9 w-9 items-center justify-center rounded-full text-foreground transition-transform active:scale-90"
              >
                <Settings className="h-[18px] w-[18px]" />
              </button>
            )}
          </div>
        </div>

        <main ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain pb-28 md:pb-8" data-testid="app-scroll">
          <SandboxBanner />
          {children}
        </main>
      </div>
    </motion.div>
  );
}
